import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart } from '../../components/charts';
import {
  Loader2, DollarSign, MousePointerClick, Eye, TrendingUp,
  BarChart3, RefreshCw, ArrowUp, ArrowDown, Layers, LayoutGrid, Megaphone
} from 'lucide-react';

import { isDemoBrand, demoHash } from '../../utils/demoBrand';

const API_BASE = 'https://api.glowytics.com';
const CRITEO_LOGO = '/CRTO-25f99ae9.png';

// Static Criteo overview for demo brands. ctr/cpc/ecpm are derived by the table
// from cost/clicks/impressions, so rows only need those three + a name.
function buildDemoCriteo() {
  const today = new Date();
  const campNames = ['Retargeting - Web', 'Prospecting - Lookalike', 'Cart Abandoners', 'Category - Fashion', 'Brand - Awareness'];

  const campaigns = campNames.map((n) => {
    const h = demoHash('crt' + n);
    const impressions = 300000 + (h % 1800000);
    const clicks = Math.round(impressions * (0.003 + (h % 10) / 1000));
    const cost = Math.round(impressions * (0.8 + (h % 45) / 100) / 100);
    return { campaign_name: n, impressions, clicks, cost };
  });

  const adsets = [];
  const ads = [];
  campaigns.forEach((c) => {
    for (let s = 0; s < 2; s++) {
      const an = `${c.campaign_name} / Set ${s + 1}`;
      const h = demoHash('crtset' + an);
      const impressions = Math.round(c.impressions / 2 * (0.8 + (h % 40) / 100));
      const clicks = Math.round(impressions * (0.003 + (h % 10) / 1000));
      const cost = Math.round(impressions * (0.8 + (h % 45) / 100) / 100);
      adsets.push({ adset_name: an, campaign_name: c.campaign_name, impressions, clicks, cost });
      for (let a = 0; a < 2; a++) {
        const adn = `${c.campaign_name} - Banner ${s + 1}.${a + 1}`;
        const ah = demoHash('crtad' + adn);
        const aimp = Math.round(impressions / 2 * (0.8 + (ah % 40) / 100));
        const acl = Math.round(aimp * (0.003 + (ah % 10) / 1000));
        const acost = Math.round(aimp * (0.8 + (ah % 45) / 100) / 100);
        ads.push({ ad_name: adn, campaign_name: c.campaign_name, adset_name: an, impressions: aimp, clicks: acl, cost: acost });
      }
    }
  });

  const daily = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    const h = demoHash('crtd' + ds);
    const impressions = 60000 + (h % 90000);
    const clicks = Math.round(impressions * (0.003 + (h % 10) / 1000));
    const cost = Math.round(impressions * (0.8 + (h % 45) / 100) / 100);
    daily.push({ date: ds, impressions, clicks, cost });
  }

  const monthly = [];
  for (let i = 7; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const h = demoHash('crtm' + m);
    const impressions = 1600000 + (h % 900000);
    const clicks = Math.round(impressions * (0.003 + (h % 10) / 1000));
    const cost = Math.round(impressions * (0.8 + (h % 45) / 100) / 100);
    monthly.push({ month: m, impressions, clicks, cost });
  }

  const totImp = campaigns.reduce((s, c) => s + c.impressions, 0);
  const totClk = campaigns.reduce((s, c) => s + c.clicks, 0);
  const totCost = campaigns.reduce((s, c) => s + c.cost, 0);
  const totals = {
    cost: totCost, clicks: totClk, impressions: totImp,
    ctr: totImp > 0 ? totClk / totImp : 0,
    cpc: totClk > 0 ? totCost / totClk : 0,
    ecpm: totImp > 0 ? (totCost / totImp) * 1000 : 0,
  };

  return { totals, campaigns, adsets, ads, daily, monthly };
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmt = (val) => new Intl.NumberFormat('en-US').format(Math.round(val || 0));
const fmtCompact = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return fmt(val);
};
const fmtCur = (val) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
const fmtDec = (val, d = 2) => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }).format(val || 0);
const pct = (val) => fmtDec((val || 0) * 100) + '%';

const TABS = [
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { id: 'adsets', label: 'Ad Sets', icon: Layers },
  { id: 'ads', label: 'Ads', icon: LayoutGrid },
];

export default function CriteoOverview() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('campaigns');
  const [sortConfig, setSortConfig] = useState({ key: 'cost', dir: 'desc' });

  const brandId = localStorage.getItem('currentBrandId');

  const fetchAll = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/criteo/db/overview`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to fetch data');
      setData((isDemoBrand(brandId) && !json?.campaigns?.length) ? buildDemoCriteo() : json);
    } catch (err) {
      console.error('Criteo fetch error:', err);
      if (isDemoBrand(brandId)) { setData(buildDemoCriteo()); setError(''); }
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totals = useMemo(() => {
    if (!data?.totals) return { spend: 0, clicks: 0, impressions: 0, ctr: 0, cpc: 0, ecpm: 0 };
    const t = data.totals;
    return {
      spend: t.cost || 0,
      clicks: t.clicks || 0,
      impressions: t.impressions || 0,
      ctr: t.ctr || 0,
      cpc: t.cpc || 0,
      ecpm: t.ecpm || 0,
    };
  }, [data]);

  const monthlyRows = useMemo(() => {
    if (!data?.monthly) return [];
    return [...data.monthly].sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);

  const dailyRows = useMemo(() => {
    if (!data?.daily) return [];
    return [...data.daily].sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const sortRows = useCallback((rows) => {
    return [...rows].sort((a, b) => {
      const av = parseFloat(a[sortConfig.key] || 0);
      const bv = parseFloat(b[sortConfig.key] || 0);
      return sortConfig.dir === 'desc' ? bv - av : av - bv;
    });
  }, [sortConfig]);

  const campaignRows = useMemo(() => sortRows(data?.campaigns || []), [data, sortRows]);
  const adsetRows = useMemo(() => sortRows(data?.adsets || []), [data, sortRows]);
  const adRows = useMemo(() => sortRows(data?.ads || []), [data, sortRows]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const dailyChartData = useMemo(() => {
    if (dailyRows.length === 0) return null;
    return {
      labels: dailyRows.map(r => {
        const d = new Date(r.date);
        return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
      }),
      datasets: [
        {
          label: 'Spend (TRY)',
          data: dailyRows.map(r => parseFloat(r.cost || 0)),
          borderColor: 'rgb(234, 88, 12)',
          backgroundColor: 'rgba(234, 88, 12, 0.08)',
          fill: true, tension: 0.3, pointRadius: 1.5, borderWidth: 2, yAxisID: 'y',
        },
        {
          label: 'Clicks',
          data: dailyRows.map(r => parseInt(r.clicks || 0)),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'transparent',
          tension: 0.3, pointRadius: 1.5, borderWidth: 2, yAxisID: 'y1',
        }
      ]
    };
  }, [dailyRows]);

  const monthlyChartData = useMemo(() => {
    if (monthlyRows.length === 0) return null;
    return {
      labels: monthlyRows.map(r => {
        const mIdx = parseInt(r.month.split('-')[1], 10) - 1;
        return MONTHS_FULL[mIdx] || r.month;
      }),
      datasets: [{
        label: 'Spend (TRY)',
        data: monthlyRows.map(r => parseFloat(r.cost || 0)),
        backgroundColor: 'rgba(234, 88, 12, 0.7)',
        hoverBackgroundColor: 'rgba(234, 88, 12, 0.9)',
        borderRadius: 8, borderSkipped: false,
      }]
    };
  }, [monthlyRows]);

  const dailyChartOptions = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, position: 'top', labels: { boxWidth: 10, usePointStyle: true, font: { size: 11 } } },
      tooltip: {
        mode: 'index', intersect: false,
        callbacks: { label: (ctx) => ctx.datasetIndex === 0 ? `Spend: ${fmtCur(ctx.parsed.y)}` : `Clicks: ${fmt(ctx.parsed.y)}` }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45, autoSkip: true, maxTicksLimit: 15 } },
      y: { position: 'left', grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 }, callback: v => fmtCur(v) } },
      y1: { position: 'right', grid: { display: false }, ticks: { font: { size: 10 } } }
    }
  };

  const monthlyChartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `Spend: ${fmtCur(ctx.parsed.y)}` } }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 }, callback: v => fmtCur(v) } }
    }
  };

  function SortableHeader({ label, sortKey }) {
    const isActive = sortConfig.key === sortKey;
    return (
      <th
        className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider cursor-pointer hover:text-gray-800 select-none"
        onClick={() => handleSort(sortKey)}
      >
        <span className="inline-flex items-center gap-1 justify-end">
          {label}
          {isActive && (sortConfig.dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
        </span>
      </th>
    );
  }

  function DataTable({ rows, showCampaign = false, showAdset = false }) {
    if (!rows || rows.length === 0) return (
      <div className="text-center py-16 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-200" />
        <p className="text-sm">No data available</p>
      </div>
    );

    const totalSpend = rows.reduce((s, r) => s + parseFloat(r.cost || 0), 0);

    return (
      <div className="overflow-x-auto w-full">
        <table className="min-w-[900px] w-full border border-gray-200">
          <thead className="bg-gray-50">
            <tr className="divide-x divide-gray-200">
              <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">Name</th>
              {showCampaign && <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">Campaign</th>}
              {showAdset && <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">Ad Set</th>}
              <SortableHeader label="Spend" sortKey="cost" />
              <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">Share</th>
              <SortableHeader label="Impressions" sortKey="impressions" />
              <SortableHeader label="Clicks" sortKey="clicks" />
              <SortableHeader label="CTR" sortKey="ctr" />
              <SortableHeader label="CPC" sortKey="cpc" />
              <SortableHeader label="eCPM" sortKey="ecpm" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => {
              const spend = parseFloat(row.cost || 0);
              const share = totalSpend > 0 ? (spend / totalSpend) * 100 : 0;
              const impressions = row.impressions || 0;
              const clicks = row.clicks || 0;
              const ctr = impressions > 0 ? clicks / impressions : 0;
              const cpc = clicks > 0 ? spend / clicks : 0;
              const ecpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
              return (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors divide-x divide-gray-100">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-[220px]">
                    <span className="truncate block">{activeTab === 'campaigns' ? row.campaign_name : activeTab === 'adsets' ? row.adset_name : row.ad_name}</span>
                  </td>
                  {showCampaign && <td className="px-3 py-3 text-xs text-gray-500 max-w-[160px] truncate">{row.campaign_name}</td>}
                  {showAdset && <td className="px-3 py-3 text-xs text-gray-500 max-w-[160px] truncate">{row.adset_name}</td>}
                  <td className="px-3 py-3 text-sm text-right font-semibold text-gray-900">{fmtCur(spend)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.min(Math.max(share, 1), 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right">{share.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmt(impressions)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmt(clicks)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{pct(ctr)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmtCur(cpc)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmtCur(ecpm)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-orange-600 animate-spin" />
          <p className="text-gray-600 text-lg animate-pulse">Loading Criteo data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
              <img src={CRITEO_LOGO} alt="Criteo" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Criteo Overview</h1>
              <p className="text-sm text-gray-500 mt-1">Ad performance overview</p>
            </div>
          </div>
        </div>
        <Card className="p-8 rounded-2xl text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={fetchAll} className="mt-4 px-5 py-2 bg-orange-100 text-orange-700 rounded-xl text-sm font-medium hover:bg-orange-200 transition">
            Retry
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 overflow-x-hidden w-full max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <img src={CRITEO_LOGO} alt="Criteo" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Criteo Overview</h1>
            <p className="text-sm text-gray-500 mt-1">Ad performance overview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="p-2 text-gray-600 hover:text-orange-600 rounded-xl hover:bg-orange-50 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Total Spend</p>
              <p className="text-xl font-bold mb-1">{fmtCur(totals.spend)}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src={CRITEO_LOGO} alt="Criteo" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Impressions</p>
              <p className="text-xl font-bold mb-1">{fmtCompact(totals.impressions)}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src={CRITEO_LOGO} alt="Criteo" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Clicks</p>
              <p className="text-xl font-bold mb-1">{fmtCompact(totals.clicks)}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src={CRITEO_LOGO} alt="Criteo" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">CTR</p>
              <p className="text-xl font-bold mb-1">{pct(totals.ctr)}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src={CRITEO_LOGO} alt="Criteo" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Avg. CPC</p>
              <p className="text-xl font-bold mb-1">{fmtCur(totals.cpc)}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src={CRITEO_LOGO} alt="Criteo" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">eCPM</p>
              <p className="text-xl font-bold mb-1">{fmtCur(totals.ecpm)}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src={CRITEO_LOGO} alt="Criteo" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Campaigns</p>
              <p className="text-xl font-bold mb-1">{campaignRows.length}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Megaphone className="h-5 w-5 text-orange-500" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Days of Data</p>
              <p className="text-xl font-bold mb-1">{dailyRows.length}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-orange-500" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dailyChartData && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Daily Trend</h2>
              <p className="text-xs text-gray-400 mt-1">Spend & clicks over time</p>
            </div>
            <div className="p-6">
              <div className="h-64">
                <LineChart data={dailyChartData} options={dailyChartOptions} />
              </div>
            </div>
          </div>
        )}
        {monthlyChartData && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Monthly Spend</h2>
              <p className="text-xs text-gray-400 mt-1">Monthly spend breakdown</p>
            </div>
            <div className="p-6">
              <div className="h-64">
                <BarChart data={monthlyChartData} options={monthlyChartOptions} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Monthly Summary Table */}
      {monthlyRows.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Monthly Summary</h2>
            <p className="text-sm text-gray-500 mt-1">Performance breakdown by month</p>
          </div>
          <div className="overflow-x-auto w-full">
            <table className="min-w-[800px] w-full border border-gray-200">
              <thead className="bg-gray-50">
                <tr className="divide-x divide-gray-200">
                  <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">Month</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">Spend</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">Impressions</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">Clicks</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">CTR</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">CPC</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">eCPM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyRows.map((row, i) => {
                  const mIdx = parseInt(row.month.split('-')[1], 10) - 1;
                  const year = row.month.split('-')[0];
                  const spend = parseFloat(row.cost || 0);
                  const impressions = row.impressions || 0;
                  const clicks = row.clicks || 0;
                  const ctr = impressions > 0 ? clicks / impressions : 0;
                  const cpc = clicks > 0 ? spend / clicks : 0;
                  const ecpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
                  const prevSpend = i > 0 ? parseFloat(monthlyRows[i-1].cost || 0) : null;
                  const change = prevSpend !== null && prevSpend > 0 ? ((spend - prevSpend) / prevSpend) * 100 : null;
                  return (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors divide-x divide-gray-100">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{MONTHS_FULL[mIdx]} {year}</td>
                      <td className="px-3 py-3 text-sm text-right font-semibold text-gray-900">
                        <div className="flex items-center justify-end gap-1.5">
                          {fmtCur(spend)}
                          {change !== null && (
                            <span className={`text-[10px] flex items-center ${change >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {change >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                              {Math.abs(change).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{fmt(impressions)}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{fmt(clicks)}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{pct(ctr)}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{fmtCur(cpc)}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{fmtCur(ecpm)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold divide-x divide-gray-200">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-900">{fmtCur(totals.spend)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmt(totals.impressions)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmt(totals.clicks)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{pct(totals.ctr)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmtCur(totals.cpc)}</td>
                  <td className="px-3 py-3 text-sm text-right text-gray-700">{fmtCur(totals.ecpm)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Campaign / AdSet / Ad Tabs */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Campaign Details</h2>
              <p className="text-sm text-gray-500 mt-1">Breakdown by campaign, ad set, and ad</p>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const count = tab.id === 'campaigns' ? campaignRows.length : tab.id === 'adsets' ? adsetRows.length : adRows.length;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'bg-white text-orange-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-600'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {activeTab === 'campaigns' && <DataTable rows={campaignRows} />}
        {activeTab === 'adsets' && <DataTable rows={adsetRows} showCampaign />}
        {activeTab === 'ads' && <DataTable rows={adRows} showCampaign showAdset />}
      </div>
    </div>
  );
}
